import {CLIENT, HAO_DOMAIN, removeLocal} from "helpers/utils.js";

const defOffset = 0;
const defLimit = 10;

export function fetchInvoices(offset = defOffset, limit = defLimit, infoFn, setRowCount, status = '', memberId = '') {
  let url = `${HAO_DOMAIN}/v1/clients/${CLIENT}/invoices?offset=${offset}&limit=${limit}`;
  if (status) url += `&status=${status}`;
  if (memberId) url += `&member_id=${memberId}`;

  return fetch(url, {method: 'GET', credentials: 'include'}).then(res => {
    if (res.status === 200) {
      return res.json();
    } else if (res.status === 401) {
      removeLocal('user');
    }
    throw new Error(`Failed to fetch invoices: ${res.status}`);
  }).then(data => {
    if (infoFn) infoFn(data?.invoices || []);
    if (setRowCount) setRowCount(data?.pagination?.total || 0);
    return data;
  }).catch(err => {
    console.error('Error fetching invoices:', err);
    if (infoFn) infoFn([]);
    if (setRowCount) setRowCount(0);
    throw err;
  });
}

export function createInvoice(invoiceData) {
  const url = `${HAO_DOMAIN}/v1/clients/${CLIENT}/invoices`;

  return fetch(url, {
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify(invoiceData)
  }).then(res => {
    if (res.status === 200) {
      return res.json();
    }
    throw new Error(`Failed to create invoice: ${res.status}`);
  }).then(data => {
    console.log('Invoice created successfully:', data);
    return data;
  }).catch(err => {
    console.error('Error creating invoice:', err);
    throw err;
  });
}

export function updateInvoice(invoiceId, invoiceData) {
  const url = `${HAO_DOMAIN}/v1/clients/${CLIENT}/invoices/${invoiceId}`;

  return fetch(url, {
    method: 'PUT',
    credentials: 'include',
    body: JSON.stringify(invoiceData)
  }).then(res => {
    if (res.status === 200) {
      return res.json();
    }
    throw new Error(`Failed to update invoice: ${res.status}`);
  }).then(data => {
    console.log('Invoice updated successfully:', data);
    return data;
  }).catch(err => {
    console.error('Error updating invoice:', err);
    throw err;
  });
}

export function deleteInvoice(invoiceId) {
  const url = `${HAO_DOMAIN}/v1/clients/${CLIENT}/invoices/${invoiceId}`;

  return fetch(url, {
    method: 'DELETE',
    credentials: 'include'
  }).then(res => {
    if (res.status === 200) {
      return true;
    }
    throw new Error(`Failed to delete invoice: ${res.status}`);
  }).then(() => {
    console.log('Invoice deleted successfully');
    return true;
  }).catch(err => {
    console.error('Error deleting invoice:', err);
    throw err;
  });
}

export function payInvoice(invoiceId, paymentData) {
  const url = `${HAO_DOMAIN}/v1/clients/${CLIENT}/invoices/${invoiceId}/pay`;

  return fetch(url, {
    method: 'PUT',
    credentials: 'include',
    body: JSON.stringify(paymentData)
  }).then(res => {
    if (res.status === 200) {
      return res.json();
    }
    throw new Error(`Failed to pay invoice: ${res.status}`);
  }).then(data => {
    console.log('Invoice paid successfully:', data);
    return data;
  }).catch(err => {
    console.error('Error paying invoice:', err);
    throw err;
  });
}

export function markInvoicePaid(invoiceId) {
  const url = `${HAO_DOMAIN}/v1/clients/${CLIENT}/invoices/${invoiceId}/mark-paid`;

  return fetch(url, {
    method: 'PUT',
    credentials: 'include'
  }).then(res => {
    if (res.status === 200) {
      return res.json();
    }
    throw new Error(`Failed to mark invoice as paid: ${res.status}`);
  }).then(data => {
    console.log('Invoice marked as paid:', data);
    return data;
  }).catch(err => {
    console.error('Error marking invoice as paid:', err);
    throw err;
  });
}

export function markInvoiceUnpaid(invoiceId) {
  const url = `${HAO_DOMAIN}/v1/clients/${CLIENT}/invoices/${invoiceId}/mark-unpaid`;

  return fetch(url, {
    method: 'PUT',
    credentials: 'include'
  }).then(res => {
    if (res.status === 200) {
      return res.json();
    }
    throw new Error(`Failed to mark invoice as unpaid: ${res.status}`);
  }).then(data => {
    console.log('Invoice marked as unpaid:', data);
    return data;
  }).catch(err => {
    console.error('Error marking invoice as unpaid:', err);
    throw err;
  });
}

export function sendInvoiceReminder(invoiceId) {
  const url = `${HAO_DOMAIN}/v1/clients/${CLIENT}/invoices/${invoiceId}/remind`;

  return fetch(url, {
    method: 'PUT',
    credentials: 'include'
  }).then(res => {
    if (res.status === 200) {
      return res.json();
    }
    throw new Error(`Failed to send reminder: ${res.status}`);
  }).then(data => {
    console.log('Reminder sent successfully:', data);
    return data;
  }).catch(err => {
    console.error('Error sending reminder:', err);
    throw err;
  });
}

export function fetchInvoiceSummary(setSummaryFn) {
  const url = `${HAO_DOMAIN}/v1/clients/${CLIENT}/invoices/summary`;

  return fetch(url, {
    method: 'GET',
    credentials: 'include'
  }).then(res => {
    if (res.status === 200) {
      return res.json();
    } else if (res.status === 401) {
      removeLocal('user');
    }
    throw new Error(`Failed to fetch invoice summary: ${res.status}`);
  }).then(data => {
    if (setSummaryFn) setSummaryFn(data);
    return data;
  }).catch(err => {
    console.error('Error fetching invoice summary:', err);
    throw err;
  });
}

export function fetchMemberInvoiceSummary(setMemberSummaryFn, memberId = '') {
  let url = `${HAO_DOMAIN}/v1/clients/${CLIENT}/invoices/member-summary`;
  if (memberId) url += `?member_id=${memberId}`;

  return fetch(url, {
    method: 'GET',
    credentials: 'include'
  }).then(res => {
    if (res.status === 200) {
      return res.json();
    } else if (res.status === 401) {
      removeLocal('user');
    }
    throw new Error(`Failed to fetch member invoice summary: ${res.status}`);
  }).then(data => {
    if (setMemberSummaryFn) setMemberSummaryFn(data);
    return data;
  }).catch(err => {
    console.error('Error fetching member invoice summary:', err);
    throw err;
  });
}

export function linkDocumentToInvoice(invoiceId, documentId) {
  const url = `${HAO_DOMAIN}/v1/clients/${CLIENT}/link/invoices/${invoiceId}/document`;

  return fetch(url, {
    method: 'PUT',
    credentials: 'include',
    body: JSON.stringify({document_id: documentId})
  }).then(res => {
    if (res.status === 200) {
      return res.json();
    }
    throw new Error(`Failed to link document to invoice: ${res.status}`);
  }).then(data => {
    console.log('Document linked to invoice successfully:', data);
    return data;
  }).catch(err => {
    console.error('Error linking document to invoice:', err);
    throw err;
  });
}

export function unlinkDocumentFromInvoice(invoiceId) {
  const url = `${HAO_DOMAIN}/v1/clients/${CLIENT}/link/invoices/${invoiceId}/document`;

  return fetch(url, {
    method: 'DELETE',
    credentials: 'include'
  }).then(res => {
    if (res.status === 200) {
      return res.json();
    }
    throw new Error(`Failed to unlink document from invoice: ${res.status}`);
  }).then(data => {
    console.log('Document unlinked from invoice successfully:', data);
    return data;
  }).catch(err => {
    console.error('Error unlinking document from invoice:', err);
    throw err;
  });
}

export function updateOverdueInvoices() {
  const url = `${HAO_DOMAIN}/v1/clients/${CLIENT}/invoices/update-overdue`;

  return fetch(url, {
    method: 'POST',
    credentials: 'include'
  }).then(res => {
    if (res.status === 200) {
      return res.json();
    }
    throw new Error(`Failed to update overdue invoices: ${res.status}`);
  }).then(data => {
    console.log('Overdue invoices updated successfully');
    return data;
  }).catch(err => {
    console.error('Error updating overdue invoices:', err);
    throw err;
  });
}
